/**
 * #344 — the Place-Order gate for the Game Detail ticket, as a pure function.
 *
 * `disabled` reproduces the boolean the ticket computed inline before the
 * extraction, clause for clause and in the same short-circuit order. `reason`
 * names whichever clause fired first; it is display/test surface only, and
 * nothing may consult it to decide whether to submit.
 *
 * A disconnected account and a missing ticker are NOT gate states — the ticket
 * early-returns a message instead of rendering the form at all.
 */
import { POLY_MIN_ORDER_CONTRACTS } from '@/lib/polymarket/closePosition';
import type { LimitRejectionBreach } from '@/lib/limits';

/** Kalshi accepts a single contract; Polymarket's CLOB rejects below 5. */
export function minQtyFor(venue: 'kalshi' | 'polymarket'): number {
  return venue === 'kalshi' ? 1 : POLY_MIN_ORDER_CONTRACTS;
}

export type SubmitBlockReason =
  | 'submitting'
  | 'limit-breach'
  | 'poly-below-floor'
  | 'ladder-no-valid-legs'
  | 'missing-qty'
  | 'missing-price';

export interface SubmitGateInput {
  submitting: boolean;
  effectiveBreach: LimitRejectionBreach | null;
  isKalshiVenue: boolean;
  count: number;
  ladderActive: boolean;
  /** `ladderSummary?.legs ?? 0` — how many rungs actually validated. */
  ladderLegs: number;
  qty: string;
  orderType: 'limit' | 'market';
  price: string;
}

export function submitGate(input: SubmitGateInput): {
  disabled: boolean;
  reason: SubmitBlockReason | null;
} {
  const { submitting, effectiveBreach, isKalshiVenue, count, ladderActive, ladderLegs, qty, orderType, price } =
    input;

  if (submitting) return { disabled: true, reason: 'submitting' };
  if (effectiveBreach) return { disabled: true, reason: 'limit-breach' };
  // Polymarket floor (belt to the blur-snap + submit-path braces). Guarded on
  // count > 0 so an empty poly ticket reports "no qty", not "below the floor".
  if (!isKalshiVenue && count > 0 && count < POLY_MIN_ORDER_CONTRACTS) {
    return { disabled: true, reason: 'poly-below-floor' };
  }
  // Ladder: enabled as long as at least one rung is valid. Single order:
  // require qty (and a price for limit) as before.
  if (ladderActive) {
    return ladderLegs === 0
      ? { disabled: true, reason: 'ladder-no-valid-legs' }
      : { disabled: false, reason: null };
  }
  if (!qty) return { disabled: true, reason: 'missing-qty' };
  if (orderType === 'limit' && !price) return { disabled: true, reason: 'missing-price' };
  return { disabled: false, reason: null };
}
